import { DayOfWeek } from 'types/datetime'
import * as Types from 'apps/marketplace/store/contractLanes/types'
import { FORMATS } from 'constants/datetime'
import { DEFAULT_RADIUS, RADIUS_RANGES } from '../constants/radii'
import {
  ANY,
  ANY_LOAD_TYPE,
  ASAP,
  CUSTOM_END_DATE,
  CUSTOM_START_DATE,
  LANE_RATE,
  RADIUS_RATE,
} from '../constants/rates'
import { getCarrierCapacity, getLocation, getPickupTimePreferences, getRadiusRates } from './rates'

const mergeRate = (
  defaults: Partial<Types.IRate>,
  existingRate?: Partial<Types.IRate>,
  carrier?: Partial<Types.ICarrier>
) =>
  Object.assign(
    defaults,
    !existingRate
      ? {}
      : {
          contractId: existingRate.contractId,
          rateType: existingRate.band ? RADIUS_RATE : LANE_RATE,
          startDate: existingRate.startDate,
          startType: existingRate.startDate ? CUSTOM_START_DATE : ASAP,
          expirationDate: existingRate.expirationDate,
          expirationType: CUSTOM_END_DATE,
          carrierId: existingRate.carrier?.id,
          carrierName: existingRate.carrier?.legalName,
          carrierCapacity: getCarrierCapacity(existingRate.carrierApplicationOfferLimits),
          dispatcher: existingRate.dispatcher?.id,
          dispatcherName: existingRate.dispatcher
            ? `${existingRate.dispatcher.firstName} ${existingRate.dispatcher.lastName}`
            : '',
          rate: existingRate.rate,
          radiusRates: getRadiusRates(defaults.radiusRates || [], existingRate.band || []),
          equipmentId: existingRate.equipment?.id,
          isDatMarketApplicable: existingRate.isDatMarketApplicable,
          loadType: existingRate.loadType,
          actionType: existingRate.actionType,
          isOnlyBeer: existingRate.isOnlyBeer || false,
          isOnlyDropTrailer: existingRate.isOnlyDropTrailer || false,
          pickupLocation: getLocation(existingRate.pickup),
          pickupRadiusMiles: existingRate.pickupRadiusMiles,
          deliveryLocation: getLocation(existingRate.delivery),
          deliveryRadiusMiles: existingRate.deliveryRadiusMiles,
          shipperId: existingRate.shipper ? existingRate.shipper.id : undefined,
          shipperName: existingRate.shipper ? existingRate.shipper.name : '',
          isShipperExclusive: existingRate.isShipperExclusive,
          deliveryTimePreference: existingRate.deliveryTimePreference,
          ...getPickupTimePreferences(existingRate.pickupTimePreferences || []),
        },
    carrier && carrier.id && carrier.legalName
      ? {
          carrierId: carrier.id,
          carrierName: carrier.legalName,
        }
      : {}
  )

/**
 * Converts lane data into something that can be consumed by the form and API.
 * Ideally this should be done on the component so we don't need this middleware logic.
 */

export const initRate = (
  laneInfo: Types.IDatLane | undefined,
  existingRate?: Partial<Types.IRate>,
  carrier?: Partial<Types.ICarrier>
) => {
  const {
    destinationReferenceCity,
    destinationReferenceState,
    id,
    originReferenceCity,
    originReferenceState,
  } = laneInfo || {}
  const { contractId } = existingRate || {}
  const laneSupportsBandedRates =
    Boolean(id) &&
    originReferenceCity === destinationReferenceCity &&
    originReferenceState === destinationReferenceState &&
    Boolean(contractId)
  return mergeRate(
    {
      laneId: id,
      laneSupportsBandedRates,
      rateType: laneSupportsBandedRates ? undefined : (LANE_RATE as Types.IRateType),
      startType: ASAP,
      radiusRates: RADIUS_RANGES.map(({ max, min }) => ({ min, max, value: '', checked: false })),
      equipmentId: 3,
      isOnlyBeer: false,
      isOnlyDropTrailer: false,
      isShipperExclusive: !carrier,
      loadType: ANY_LOAD_TYPE,
      actionType: Types.RATE_ACTION_TYPE.reserved,
      pickupLocation: {
        city: originReferenceCity || '',
        state: originReferenceState || '',
        stateCode: originReferenceState || '',
      },
      pickupRadiusMiles: DEFAULT_RADIUS,
      deliveryLocation: {
        city: destinationReferenceCity || '',
        state: destinationReferenceState || '',
        stateCode: destinationReferenceState || '',
      },
      deliveryRadiusMiles: DEFAULT_RADIUS,
      deliveryTimePreference: ANY,
      pickupTimeType: ANY,
      pickupTimePreferences: FORMATS.fullWeekList.map(dayOfWeek => {
        const dayOfWeekCast = dayOfWeek as DayOfWeek
        return {
          dayOfWeek: dayOfWeekCast,
          startTime: '',
          endTime: '',
        }
      }),
    },
    existingRate,
    carrier
  )
}
